import { Flex, FlexProps, Heading } from '@chakra-ui/react'
import { ReactNode } from 'react'

/**
 * Espaço entre o título e o conteúdo da seção. Era `mb={4}` em 11 sites e `mb={5}` em 8, para a
 * mesma coisa — inclusive nos dois cards lado a lado do `EventDetailPage`, que assim começavam
 * o conteúdo em alturas diferentes.
 */
export const SECTION_TITLE_MB = 4

/**
 * Forma do título, não uma medida do Chakra — a mesma ideia do `size` do `FormModal` e do
 * `LoadingBlock`. `section` abre uma seção da página; `mini` é o rótulo que separa blocos dentro
 * de uma gaveta de detalhe, onde o título da seção inteira já é o cabeçalho da gaveta.
 */
export type SectionTitleVariant = 'section' | 'mini'

// `fontSize` e não `size`: a escala do `<Heading>` vem do nível (`theme.ts`), e este título é
// um **rótulo** em caixa alta, não a prosa de um nível. Um componente com forma própria declara
// a medida; uma página, não — a mesma divisão do `contentProps` do `FormModal`.
const VARIANTS = {
  section: { fontSize: 'md', color: 'gray.700', letterSpacing: 'wide', mb: SECTION_TITLE_MB, as: 'h2' },
  mini: { fontSize: 'sm', color: 'gray.400', letterSpacing: 'wider', mb: 2, as: 'h3' }
} as const

export interface ISectionTitleProps extends Omit<FlexProps, 'as' | 'title'> {
  /** O texto do título, em pt-BR. Vai para caixa alta pelo próprio componente. */
  children?: ReactNode
  /** Ações à direita do título: um botão, um selo, um contador. */
  actions?: ReactNode
  /**
   * Nível do heading. `h2` é o certo para uma seção da página, porque o único `h1` é o do
   * `PageTitle`; desça para `h3` só quando a seção estiver dentro de outra seção titulada.
   * Sem ele o nível vem da `variant`.
   */
  as?: 'h2' | 'h3' | 'h4'
  /** Forma do título. `section` (padrão) abre uma seção; `mini` separa blocos numa gaveta. */
  variant?: SectionTitleVariant
}

/**
 * Título de uma seção do painel — a linha que abre um `<SectionCard>` ou encabeça uma grade
 * de cards.
 *
 * Havia seis grafias para ele, e a divergência não era só estética. Duas eram `<Heading>`
 * (`size="sm"` em `gray.700` com `letterSpacing`, e `size="sm"` em `gray.500` sem) e **quatro
 * eram `<Text>`** — `fontWeight="bold" fontSize="sm"` no `ContactSnippetPage`, `fontSize="xs"
 * fontWeight="bold"` em `gray.400` no `SupportViewPage`, e `fontSize="md" fontWeight="bold"` em
 * `gray.800` (mais um `fontSize="sm"`) no módulo financeiro. Nessas páginas, mais o
 * `FinancialIndicatorsSection`, o `PlrSection` e o `PerformanceChart`, não existe **um único
 * `<Heading>` no arquivo inteiro**: o documento pula do `h1` do `PageTitle` direto para o
 * conteúdo e as seções não aparecem no outline. É o mesmo tipo de defeito que o eixo de loading
 * encontrou — a UI divergente era o sintoma, a ausência era o problema.
 *
 * A página declara o texto e o nível; a aparência mora aqui. **O nível é explícito de
 * propósito, e é o que mais escapa**: um card dentro de outro card titulado precisa de `as`
 * (ou de `titleAs`, no `SectionCard`) para não emitir um segundo `h2` aninhado — foi o que
 * aconteceu com o `PerformanceChart` e o `PlrSection` na review desta própria migração.
 */
export default function SectionTitle({ children, actions, as, variant = 'section', ...rest }: ISectionTitleProps) {
  const shape = VARIANTS[variant]

  return (
    // `wrap`: uma barra de ações cheia (a de "Inscritos" do painel do evento tem busca, três
    // filtros e dois botões) desce para a linha de baixo em vez de espremer o título. Os
    // cabeçalhos mais carregados já passavam `flexWrap="wrap"` à mão por esse motivo.
    <Flex
      align="center"
      justify={children == null ? 'flex-end' : 'space-between'}
      wrap="wrap"
      gap={3}
      mb={shape.mb}
      {...rest}
    >
      {children != null && (
        // `minW={0}` deixa o título encolher em vez de empurrar as ações para fora da linha.
        <Heading
          as={as ?? shape.as}
          fontSize={shape.fontSize}
          color={shape.color}
          textTransform="uppercase"
          letterSpacing={shape.letterSpacing}
          minW={0}
        >
          {children}
        </Heading>
      )}
      {actions}
    </Flex>
  )
}
